import styled, { css } from 'styled-components';
import { loading } from '../../theme/animations';
import { Icon } from '../Icon';
import type { ButtonTypes } from './index.d';

const SIZE_PADDINGS = {
    xSmall: {
        onlyText: '7px 16px',
        textAndIcon: '7px 16px 7px 36px',
        onlyIcon: '8px',
    },
    small: {
        onlyText: '11px 16px',
        textAndIcon: '11px 16px 11px 48px',
        onlyIcon: '8px',
    },
    medium: {
        onlyText: '14px 24px',
        textAndIcon: '14px 16px 14px 48px',
        onlyIcon: '12px',
    },
    large: {
        onlyText: '16px 24px',
        textAndIcon: '16px 24px 16px 64px',
        onlyIcon: '12px',
    },
};
export const BtnWrapper = styled.span<{ placement?: 'flex-start' | 'center' | 'flex-end' }>`
    display: flex;
    justify-content: ${props => props.placement};
`;

export const IconWrapper = styled.span`
    display: inline-block;
    width: 16px;
    height: 16px;
    background: rgba(255, 255, 255, 0.06);
    border-radius: 50%;
    box-shadow: 0 0 0 21px rgba(255, 255, 255, 0.06);
`;

export const Btn = styled.button<{
    $variant: ButtonTypes;
    $fullWidth?: boolean;
    $color?: ButtonTypes;
    $size?: 'xSmall' | 'small' | 'medium' | 'large';
    $buttonType?: 'onlyText' | 'textAndIcon' | 'onlyIcon';
    $keepStylesWhenDisabled?: boolean;
}>`
    position: relative;
    color: ${props =>
        props.$color ? props.theme.root.button[props.$color].color : props.theme.root.button[props.$variant].color};
    background: ${props => props.theme.root.button[props.$variant].background};
    border-radius: ${props => props.theme.root.button[props.$variant].borderRadius};
    border: ${props => props.theme.root.button[props.$variant].border};
    padding: ${(
        props, // если заданы size и buttonType, то берутся паддинги для нужных размеров
    ) =>
        props.$size && props.$buttonType
            ? SIZE_PADDINGS[props.$size][props.$buttonType]
            : props.$size && !props.$buttonType
            ? SIZE_PADDINGS[props.$size]['onlyText']
            : props.theme.root.button[props.$variant].padding};
    cursor: pointer;
    outline: none;
    fontsize: ${props => (props.$size === 'small' ? '14px' : props.$size === 'medium' ? '16px' : '20px')};
    lineheight: ${props => (props.$size === 'small' ? '18px' : props.$size === 'medium' ? '20px' : '24px')};
    align-items: center;

    ${props =>
        props.$fullWidth &&
        css`
            width: 100%;
        `}
    &:hover {
        background: ${props => props.theme.root.button[props.$variant].hoverBackground};
    }

    &:focus-visible {
        background: ${props => props.theme.root.button[props.$variant].focusBackground};
    }

    &:active {
        background: ${props => props.theme.root.button[props.$variant].activeBackground};
    }

    &:disabled {
        ${props =>
            !props.$keepStylesWhenDisabled &&
            css`
                cursor: default;
                color: ${props.theme.root.colors.basicColors['30%']};
                background: ${props.theme.root.button[props.$variant].disabledBackground};
            `}
    }

    &:focus-visible,
    &:hover,
    &:active {
        ${IconWrapper} {
            background: transparent;
            box-shadow: none;
        }
    }
`;

const activeIconStyle = css`
    /*    svg {
        -webkit-filter: invert(100%); !* safari 6.0 - 9.0 *!
        filter: invert(100%);
    }*/

    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        display: block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        border: 2px solid ${props => props.theme.root.colors.background['02Second']};
        background: ${props => props.theme.root.colors.primary.default};
    }
`;

export interface IconButtonStylesProps {
    isActive?: boolean;
    size?: string;
    noHover?: boolean;
}

export const iconButtonStyles = css<IconButtonStylesProps>`
    ${props => props.isActive && activeIconStyle};
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    background-color: transparent;
    width: ${props => (props.size === 'small' ? '24px' : '32px')};
    height: ${props => (props.size === 'small' ? '24px' : '32px')};
    padding: 0;
    border: none;
    outline: none;
    cursor: pointer;

    &:before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: block;
        width: ${props => (props.size === 'small' ? '32px' : '40px')};
        height: ${props => (props.size === 'small' ? '32px' : '40px')};
        border-radius: 50%;
        box-sizing: border-box;
        background-color: transparent;
    }

    &:hover {
        path,
        rect {
            fill: ${props => props.theme.root.colors.basicColors['55%']};
        }
    }

    &:hover,
    &:focus {
        &:before {
            background-color: ${props => (props.noHover ? 'transparent' : props.theme.root.colors.basicColors['6%'])};
        }
    }

    &:active {
        &:before {
            background-color: ${props => (props.noHover ? 'transparent' : props.theme.root.colors.basicColors['10%'])};
        }
    }

    &:disabled {
        cursor: default;

        path,
        rect {
            fill: ${props => props.theme.root.colors.basicColors['80%']};
        }

        &:before {
            background-color: transparent;
            cursor: default;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

export const IconButtonNone = styled.button<IconButtonStylesProps>`
    ${iconButtonStyles}
`;

export const IconButtonDefault = styled.button<IconButtonStylesProps>`
    ${iconButtonStyles};

    path,
    rect {
        fill: ${props => props.theme.root.colors.basicColors['55%']};
    }
`;

export const IconButtonSecondary = styled.button<IconButtonStylesProps>`
    ${iconButtonStyles};

    path,
    rect {
        fill: ${props => props.theme.root.colors.basicColors['30%']};
    }

    &:hover {
        path,
        rect {
            fill: ${props => props.theme.root.colors.basicColors['55%']};
        }
    }
`;

export const IconButtonThird = styled.button<IconButtonStylesProps>`
    ${iconButtonStyles};

    path,
    rect {
        fill: ${props => props.theme.root.colors.basicColors['30%']};
    }
`;

export const IconButtonDisabled = styled.button<IconButtonStylesProps>`
    ${iconButtonStyles};

    path,
    rect {
        fill: ${props => props.theme.root.colors.basicColors['80%']};
    }

    &:hover {
        path,
        rect {
            fill: ${props => props.theme.root.colors.basicColors['80%']};
        }
    }
`;

export const IconButtonColored = styled.button<IconButtonStylesProps>`
    ${iconButtonStyles};

    path,
    rect {
        fill: ${props => props.theme.root.colors.accent.textOrIcon};
    }
`;

export const IconButtonOnButton = styled.button<IconButtonStylesProps>`
    ${iconButtonStyles};

    path,
    rect {
        fill: ${props => props.theme.root.colors.accent.textOrIcon};
    }
`;

export const LoadingWrapper = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    justify-content: center;
    align-items: center;
`;

export const ButtonText = styled.span<{ $isVisible?: boolean }>`
    visibility: ${props => (props.$isVisible ? 'visible' : 'hidden')};
`;

export const LoadingButtonIcon = styled(Icon)`
    ${loading}
`;
